import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { AUTH_STORAGE_KEY, supabase } from "../lib/supabase";
import type { Profile } from "../lib/types";
import { needsSecondStep } from "./mfa";

interface AuthState {
  /** null until the stored session has been read. */
  ready: boolean;
  session: Session | null;
  profile: Profile | null;
  /**
   * Signed in with the password, but the account uses two-step verification and the 6-digit code from the
   * authenticator app hasn't been entered yet (the session is aal1): the code step, never the panel. The profile isn't
   * read until then (the database refuses it anyway).
   */
  mfaNeeded: boolean;
  /** Signed in but the account has no profile yet (made outside the app): send through setup. */
  needsSetup: boolean;
  /** The profile couldn't be read (offline, server down): shown with a retry instead of a spinner. */
  profileError: string | null;
  /**
   * A password reset is under way: the emailed code made a session, but the new password isn't saved yet. While
   * set, #/forgot-password keeps the screen instead of sending the signed-in session to the panel. Kept for the tab
   * (sessionStorage), so a reload mid-reset comes back to the new-password step.
   */
  resetting: boolean;
  setResetting: (on: boolean) => void;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const RESETTING_KEY = "kept.resetting";

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(RESETTING_KEY) === "1";
  } catch {
    return false;
  }
}

function messageOf(e: unknown): string {
  if (e && typeof e === "object" && "message" in e && typeof e.message === "string" && e.message) return e.message;
  return "Couldn't reach Keept's server.";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [resetting, setResettingState] = useState(() => readFlag());
  const setResetting = useCallback((on: boolean) => {
    setResettingState(on);
    try {
      if (on) sessionStorage.setItem(RESETTING_KEY, "1");
      else sessionStorage.removeItem(RESETTING_KEY);
    } catch {
      /* storage off: the flag lives for this page only */
    }
  }, []);
  // Loads can overlap (stored session + SIGNED_IN event, a quick account switch): only the latest lands.
  const loadSeq = useRef(0);
  const loadedFor = useRef<string | null>(null);

  const loadProfile = useCallback(async (s: Session | null) => {
    const mine = ++loadSeq.current;
    const uid = s?.user.id ?? null;
    if (uid !== loadedFor.current) {
      // Another account (or none): the previous one's name and role must not show for a moment.
      loadedFor.current = uid;
      setProfile(null);
      setNeedsSetup(false);
    }
    setProfileError(null);
    if (!s || needsSecondStep(s)) return;
    try {
      const { data, error } = await supabase.from("profiles")
        .select("role, email, display_name, company_name, ein, business_type").eq("user_id", s.user.id).maybeSingle();
      if (error) throw error;
      if (mine !== loadSeq.current) return;
      if (!data) {
        // No profile: either an account made outside the app (finish setup), or one deleted since this
        // browser signed in, whose token is still valid for a while. Ask the server which.
        const { error: userError } = await supabase.auth.getUser();
        if (mine !== loadSeq.current) return;
        const status = (userError as { status?: number } | null)?.status ?? 0;
        if (userError && status >= 400 && status < 500) {
          await supabase.auth.signOut({ scope: "local" });
          return;
        }
      }
      setProfile(data as Profile | null);
      setNeedsSetup(!data);
    } catch (e) {
      if (mine !== loadSeq.current) return;
      setProfileError(messageOf(e));
    }
  }, []);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return;
      setSession(data.session);
      await loadProfile(data.session);
      if (alive) setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED" || event === "MFA_CHALLENGE_VERIFIED") void loadProfile(s);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value: AuthState = {
    ready,
    session,
    mfaNeeded: needsSecondStep(session),
    profile,
    needsSetup,
    profileError,
    resetting,
    setResetting,
    refreshProfile: () => loadProfile(session),
    signOut: async () => {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // Offline or the server didn't answer: supabase keeps the session then. This device forgets it
        // anyway (the token expires on its own); a fresh start shows the sign-in screen.
        try {
          localStorage.removeItem(AUTH_STORAGE_KEY);
        } catch {
          /* nothing stored */
        }
        location.reload();
      }
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
